(function (factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else if (typeof window !== 'undefined') api.mount(window);
})(function () {
  'use strict';
  function fall(progress) {
    const p = Math.max(0, Math.min(1, progress));
    const stops = [[0, 0], [.16, -.72], [.46, .12], [.68, -.08], [1, 0]];
    for (let i = 1; i < stops.length; i++) if (p <= stops[i][0]) {
      const [a, from] = stops[i - 1], [b, to] = stops[i];
      const t = (p - a) / (b - a), ease = t * t * (3 - 2 * t);
      return from + (to - from) * ease;
    }
    return 0;
  }
  function regions(geometry, single) {
    if (!geometry?.width || !geometry?.height) return [];
    return (single ? ['one'] : ['one', 'amp', 'two']).map(key => {
      const point = geometry[key];
      if (!point?.height) return null;
      return { x: point.x / geometry.width, y: (point.base - point.height * .49) / geometry.height,
        rx: point.height * .68 / geometry.width, ry: point.height * .64 / geometry.height };
    }).filter(Boolean);
  }
  function renderer(canvas) {
    const gl = canvas.getContext('webgl', { alpha: false, antialias: false, depth: false, powerPreference: 'low-power' });
    if (!gl) return null;
    const shaders = [];
    const compile = (type, source) => {
      const shader = gl.createShader(type); gl.shaderSource(shader, source); gl.compileShader(shader); shaders.push(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error('Photo animation unavailable');
      return shader;
    };
    let program, buffer, texture;
    const destroy = () => {
      if (texture) gl.deleteTexture(texture);
      if (buffer) gl.deleteBuffer(buffer);
      if (program) gl.deleteProgram(program);
      shaders.forEach(shader => gl.deleteShader(shader));
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    };
    try {
      program = gl.createProgram();
      gl.attachShader(program, compile(gl.VERTEX_SHADER, 'attribute vec2 position; varying vec2 uv; void main(){uv=position*.5+.5; gl_Position=vec4(position.x,-position.y,0.,1.);}'));
      gl.attachShader(program, compile(gl.FRAGMENT_SHADER, `precision highp float;
        uniform sampler2D photo; uniform vec4 region[3]; uniform vec2 shift[3]; varying vec2 uv;
        void main(){ vec2 offset=vec2(0.);
          for(int i=0;i<3;i++){
            vec2 local=abs((uv-region[i].xy)/max(region[i].zw,vec2(.0001)));
            float weight=(1.-smoothstep(.70,1.,local.x))*(1.-smoothstep(.76,1.,local.y));
            offset+=shift[i]*weight;
          }
          gl_FragColor=texture2D(photo,clamp(uv-offset,vec2(0.),vec2(1.)));
        }`));
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error('Photo animation unavailable');
      gl.useProgram(program);
      buffer = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1,1,-1,-1,1,1,1]), gl.STATIC_DRAW);
      const position = gl.getAttribLocation(program, 'position');
      gl.enableVertexAttribArray(position); gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
      texture = gl.createTexture(); gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      const area = gl.getUniformLocation(program, 'region[0]'), shift = gl.getUniformLocation(program, 'shift[0]');
      return {
        load(image, points, width, height) {
          const ratio = Math.min(2, Math.sqrt(4000000 / (width * height)));
          canvas.width = Math.max(1, Math.round(width * ratio)); canvas.height = Math.max(1, Math.round(height * ratio));
          gl.viewport(0, 0, canvas.width, canvas.height);
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, image);
          const packed = new Float32Array(12);
          points.forEach((point, i) => packed.set([point.x, point.y, point.rx, point.ry], i * 4));
          gl.uniform4fv(area, packed);
        },
        draw(elapsed, width, height) {
          const offsets = new Float32Array(6), distance = Math.min(18, Math.max(9, width * .009));
          for (let i = 0; i < 3; i++) {
            const motion = fall((elapsed - i * 48) / 620);
            offsets[i * 2] = motion * (i === 1 ? -1.6 : 1.6) / width;
            offsets[i * 2 + 1] = motion * distance / height;
          }
          gl.uniform2fv(shift, offsets); gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        }, destroy
      };
    } catch (_) { destroy(); return null; }
  }
  function mount(win) {
    const doc = win.document, instances = new Map();
    const reduced = win.matchMedia('(prefers-reduced-motion: reduce)');
    function initialize(scope) {
      const roots = scope.matches?.('[data-fas-garden]') ? [scope] : scope.querySelectorAll('[data-fas-garden]');
      roots.forEach(root => {
        if (instances.has(root)) return;
        const form = root.querySelector('[data-fas-form]'), note = root.querySelector('[data-fg-note]');
        if (!form) return;
        const abort = new win.AbortController(), canvas = doc.createElement('canvas');
        canvas.className = 'fg-photo-settle'; canvas.setAttribute('aria-hidden', 'true'); canvas.hidden = true;
        let gpu = null, frame = null, scheduled = null, epoch = 0, last = '', pending = '', dead = false, noteAnimation = null, activeMode = '';
        let customAnimations = [];
        const cache = new Map();
        const stop = () => {
          epoch++; pending = ''; if (frame !== null) win.cancelAnimationFrame(frame); frame = null;
          canvas.hidden = true; delete root.dataset.fgPhotoSettling;
          delete root.dataset.fgCustomSettling; activeMode = '';
          customAnimations.forEach(animation => animation.cancel()); customAnimations = [];
          noteAnimation?.cancel(); noteAnimation = null;
        };
        const animateNote = () => {
          if (!note || note.hidden || reduced.matches || root.dataset.fgEditing || doc.hidden) return;
          noteAnimation?.cancel();
          noteAnimation = note.animate([
            { translate: '0 0', rotate: '0deg' }, { translate: '0 -10px', rotate: '-1.4deg', offset: .16 },
            { translate: '0 1px', rotate: '.5deg', offset: .48 }, { translate: '0 -1px', rotate: '-.2deg', offset: .7 },
            { translate: '0 0', rotate: '0deg' }
          ], { duration: 740, easing: 'ease-in-out' });
        };
        const read = selector => { try { return JSON.parse(root.querySelector(selector)?.textContent || '[]'); } catch (_) { return []; } };
        const large = read('[data-fg-scene-geometry]'), small = read('[data-fg-small-scene-geometry]'), single = read('[data-fg-single-scene-geometry]');
        const animateCustom = selection => {
          const targets = [root.querySelector('[data-fg-letter="1"] [data-fg-letter-canvas]')];
          if (selection.offer !== 'single') targets.push(root.querySelector('.fg-amp-crop'), root.querySelector('[data-fg-letter="2"] [data-fg-letter-canvas]'));
          const distance = Math.min(18, Math.max(9, root.getBoundingClientRect().width * .009));
          const token = ++epoch;
          customAnimations = targets.filter(target => target && !target.hidden && win.getComputedStyle(target.parentElement).opacity !== '0').map((target, index) => {
            const frames = Array.from({ length: 41 }, (_, step) => {
              const motion = fall(step / 40);
              return { offset: step / 40, translate: (motion * (index === 1 ? -1.6 : 1.6)) + 'px ' + (motion * distance) + 'px' };
            });
            return target.animate(frames, { duration: 620, delay: index * 48, easing: 'linear' });
          });
          if (!customAnimations.length) return;
          activeMode = 'custom'; root.dataset.fgCustomSettling = 'true'; animateNote();
          Promise.all(customAnimations.map(animation => animation.finished)).then(() => { if (token === epoch) stop(); }, () => {});
        };
        async function update() {
          scheduled = null;
          if (dead) return;
          const selection = Object.fromEntries(new win.FormData(form));
          const scene = Math.max(0, Math.min(3, Number(root.dataset.scene) || 0));
          const kind = root.dataset.fgPhotoSize, portrait = win.matchMedia('(max-aspect-ratio: 1/1)').matches;
          const key = [selection.offer, selection.size, scene, portrait, selection.letter1, selection.letter2, selection.tone1, selection.tone2, selection.theme1, selection.theme2].join(':');
          const changed = last && key !== last;
          if (key !== last) { stop(); last = key; if (changed) pending = key; }
          if (reduced.matches || doc.hidden || root.dataset.fgEditing) { stop(); return; }
          if (!pending) return;
          if (root.dataset.fgPhotoMode === 'custom') {
            if (root.dataset.fgLetterQuality !== 'ready') return;
            pending = ''; animateCustom(selection); return;
          }
          if (root.dataset.fgPhotoMode !== 'integrated') return;
          const room = root.querySelector('[data-fg-room="' + scene + '"]');
          const selector = kind === 'small' ? '.fg-small-integrated-photo' : kind === 'single-small' ? '.fg-single-small-photo' : kind === 'single-large' ? '.fg-single-large-photo' : '.fg-integrated-photo';
          const source = room?.querySelector(selector), plane = room?.querySelector('.fg-photo-plane');
          if (!source?.complete || !source.naturalWidth || !plane) return;
          const mode = portrait ? 'mobile' : 'desktop';
          const geometry = selection.offer === 'single' ? single[scene]?.[mode]?.[selection.size === 'small' ? 'small' : 'large'] : (selection.offer === 'pareja' ? small : large)[scene]?.[mode];
          const points = regions(geometry, selection.offer === 'single');
          if (!points.length) { stop(); return; }
          pending = ''; const token = ++epoch, url = source.currentSrc || source.src;
          let image = cache.get(url);
          if (!image) {
            image = new win.Image(); image.crossOrigin = 'anonymous'; image.src = url;
            try { await image.decode(); } catch (_) { return; }
            if (dead || token !== epoch) return;
            cache.set(url, image); if (cache.size > 3) cache.delete(cache.keys().next().value);
          }
          if (token !== epoch || root.dataset.fgPhotoMode !== 'integrated' || root.dataset.fgEditing || reduced.matches) return;
          gpu ||= renderer(canvas);
          if (!gpu) return;
          const { width, height } = plane.getBoundingClientRect();
          if (!width || !height) return;
          try { gpu.load(image, points, width, height); } catch (_) { stop(); return; }
          canvas.style.filter = win.getComputedStyle(source).filter;
          plane.appendChild(canvas); gpu.draw(0, width, height); canvas.hidden = false;
          activeMode = 'integrated'; root.dataset.fgPhotoSettling = 'true'; animateNote();
          const started = win.performance.now();
          function tick(now) {
            if (dead || token !== epoch) return;
            const elapsed = now - started;
            if (elapsed >= 740) { stop(); return; }
            gpu.draw(elapsed, width, height); frame = win.requestAnimationFrame(tick);
          }
          frame = win.requestAnimationFrame(tick);
        }
        const schedule = () => { if (!dead && scheduled === null) scheduled = win.requestAnimationFrame(update); };
        const on = (target, name, fn) => target.addEventListener(name, fn, { signal: abort.signal });
        const observer = new win.MutationObserver(() => {
          if (activeMode && root.dataset.fgPhotoMode !== activeMode) stop();
          schedule();
        });
        observer.observe(root, { attributes: true, attributeFilter: ['data-scene', 'data-fg-photo-mode', 'data-fg-photo-size', 'data-fg-editing', 'data-fg-letter-quality'] });
        const noteObserver = new win.MutationObserver(() => { if (note.hidden) noteAnimation?.cancel(); else animateNote(); });
        if (note) noteObserver.observe(note, { attributes: true, attributeFilter: ['hidden'] });
        on(form, 'change', schedule); on(root, 'fas:selection', event => {
          if (event.detail?.source === 'confirmed-editor') { stop(); pending = last; }
          schedule();
        });
        on(root, 'load', schedule); on(win, 'resize', () => { stop(); schedule(); });
        on(reduced, 'change', () => { stop(); schedule(); }); on(doc, 'visibilitychange', stop);
        on(canvas, 'webglcontextlost', event => { event.preventDefault(); stop(); });
        instances.set(root, () => { dead = true; stop(); abort.abort(); observer.disconnect(); noteObserver.disconnect(); if (scheduled !== null) win.cancelAnimationFrame(scheduled); gpu?.destroy(); canvas.remove(); cache.clear(); });
        update();
      });
    }
    doc.addEventListener('shopify:section:load', event => initialize(event.target));
    doc.addEventListener('shopify:section:unload', event => { for (const [root, cleanup] of instances) if (event.target === root || event.target.contains(root)) { cleanup(); instances.delete(root); } });
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', () => initialize(doc), { once: true });
    else initialize(doc);
  }
  return { fall, regions, mount };
});
